import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { ArrowUpRight, Check, X } from "@phosphor-icons/react";
import { ClipPhoto, MaskWords, PageFrame, Photo, Seo, useMediaQuery } from "../../kit.jsx";
import { verifiedPartnerRelationships } from "../../data/content.js";
import { Btn, Count, GoLink, Rise } from "./pgkit.jsx";
import { ClipVideo } from "./media.jsx";
import { PhotoGallery } from "./AarohGallery.jsx";
import { METAMORPHOSIS_ALBUM, METAMORPHOSIS_CLIPS } from "../../data/metamorphosis.js";
import "./programmes.css";

const SITE = "https://metamorphosis.taleofhumankind.org/";

const pick = (id) => METAMORPHOSIS_ALBUM.find((photo) => photo.id === id) ?? METAMORPHOSIS_ALBUM[0];
const clip = (id) => METAMORPHOSIS_CLIPS.find((item) => item.id === id) ?? METAMORPHOSIS_CLIPS[0];
const wallItem = (id) => {
  const photo = pick(id);
  return [photo.src, photo.alt, photo.caption, photo.height > photo.width ? "tall" : "wide"];
};

const WALL = [
  [wallItem("679"), wallItem("683")],
  [wallItem("510"), wallItem("522")],
  [wallItem("538"), wallItem("509")],
];

const MOMENTS = ["264", "257", "228"].map(clip);

const GALLERY_CATEGORIES = [
  ["all", "Everything"],
  ["workshops", "Workshops and showcase"],
  ["speakers", "Talks"],
  ["teams", "Teams"],
  ["awards", "Certificates"],
];

function Hero() {
  const [toh, iitd] = verifiedPartnerRelationships;
  return (
    <section className="pg-mh" aria-labelledby="pg-mh-title">
      <Rise as="p" className="gh-mono pg-kicker" onMount>Metamorphosis · IIT Delhi · May 2026</Rise>
      <MaskWords
        as="h1"
        id="pg-mh-title"
        text="Led by Tale of Humankind Foundation and IIT Delhi. GRID was the outreach partner."
        onMount
        delay={0.05}
      />
      <div className="pg-mh__row">
        <Rise as="p" onMount delay={0.45}>
          Metamorphosis brought high-school students to IIT Delhi to find real problems and build early responses. The organisers ran it. GRID&apos;s part was outreach: helping students find the programme and take part.
        </Rise>
        <Rise className="gh-actions" onMount delay={0.55}>
          <Btn href={SITE} icon={<ArrowUpRight weight="bold" aria-hidden="true" className="pg-btn__after" />}>Visit Metamorphosis</Btn>
          <Btn href="#pg-meta-role" variant="ghost">What outreach meant</Btn>
        </Rise>
      </div>
      <Rise as="ol" className="pg-roles" onMount delay={0.65} aria-label="Who did what">
        {[toh, iitd].map((partner) => (
          <li key={partner.name} className="pg-roles__org">
            <span className="pg-roles__logo"><img src={partner.logo} alt="" width={partner.logoWidth} height={partner.logoHeight} /></span>
            <span className="gh-mono">Organiser</span>
            <strong>{partner.name}</strong>
          </li>
        ))}
        <li className="pg-roles__grid">
          <span className="pg-roles__logo"><img src="/images/brand/grid-mark.png" alt="" width="704" height="414" /></span>
          <span className="gh-mono">Outreach partner</span>
          <strong>Project GRID</strong>
        </li>
      </Rise>
      <Rise className="pg-mh__film" onMount delay={0.8}>
        <ClipVideo clip={clip("614")} className="pg-mh__video" eager />
        <span className="pg-mh__filmcap">{clip("614").caption}</span>
      </Rise>
    </section>
  );
}

function WallItem({ item }) {
  const [src, alt, caption, shape] = item;
  return (
    <figure className={`pg-wall__item pg-wall__item--${shape}`}>
      <span><Photo src={src} alt={alt} sizes="(max-width: 899px) 100vw, 33vw" /></span>
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

function WallColumn({ items, progress, distance, enabled }) {
  const y = useTransform(progress, (value) => (value - 0.5) * distance);
  return (
    <motion.div className="pg-wall__col" style={enabled ? { y } : undefined}>
      {items.map((item) => <WallItem key={item[0]} item={item} />)}
    </motion.div>
  );
}

function CrowdWall() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const wide = useMediaQuery("(min-width: 900px)");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const enabled = wide && !reduceMotion;
  return (
    <section className="pg-wall" aria-labelledby="pg-wall-title">
      <header className="pg-wall__head">
        <MaskWords id="pg-wall-title" text="The room at IIT Delhi." />
        <p>One week at IIT Delhi, 18 to 23 May 2026: workshops, machine shop visits, talks and a project showcase.</p>
      </header>
      <ClipPhoto
        className="pg-wall__lead"
        src={pick("745").src}
        alt={pick("745").alt}
        caption={pick("745").caption}
        sizes="100vw"
      />
      <div className="pg-wall__cols" ref={ref}>
        {WALL.map((items, index) => (
          <WallColumn key={index} items={items} progress={scrollYProgress} distance={[-140, 90, -60][index]} enabled={enabled} />
        ))}
      </div>
    </section>
  );
}

function Moments() {
  return (
    <section className="pg-mmom" aria-labelledby="pg-mmom-title">
      <header className="pg-mmom__head">
        <MaskWords id="pg-mmom-title" text="Built, wired and shown." />
        <p>Short clips from the project showcase and the workshops.</p>
      </header>
      <ul className="pg-mmom__row">
        {MOMENTS.map((item, index) => (
          <Rise as="li" key={item.id} delay={index * 0.08}>
            <ClipVideo clip={item} className="pg-mmom__video" />
            <span>{item.caption}</span>
          </Rise>
        ))}
      </ul>
    </section>
  );
}

function Gallery() {
  return (
    <div className="pg-mgal" data-nav-tone="dark">
      <PhotoGallery
        id="pg-mgal"
        photos={METAMORPHOSIS_ALBUM}
        categories={GALLERY_CATEGORIES}
        title="One week in photos."
        intro={(count) => `${count} photographs from Metamorphosis at IIT Delhi. Pick a part of the week, or open any photo to see it large.`}
      />
    </div>
  );
}

function Figures() {
  return (
    <section className="pg-mfig" aria-labelledby="pg-mfig-title">
      <h2 id="pg-mfig-title" className="sr-only">Programme figures</h2>
      <div className="pg-mfig__pair">
        <Rise>
          <strong><Count to={150} /></strong>
          <span>students</span>
        </Rise>
        <Rise delay={0.08}>
          <strong><Count to={36} /></strong>
          <span>products built</span>
        </Rise>
      </div>
      <p>
        150 students from 18 states spent one week at IIT Delhi and built 36 products.
      </p>
    </section>
  );
}

function Role() {
  return (
    <section className="pg-mrole" id="pg-meta-role" aria-labelledby="pg-mrole-title">
      <header className="pg-mrole__head">
        <MaskWords id="pg-mrole-title" text="What outreach meant." />
        <p>Outreach is a small, specific job. Here is the line between our part and the organisers&apos; part.</p>
      </header>
      <div className="pg-mrole__cols">
        <Rise className="pg-mrole__col">
          <h3>What GRID did</h3>
          <ul>
            <li><Check weight="bold" aria-hidden="true" /><span>Helped high-school students hear about Metamorphosis and take part.</span></li>
            <li><Check weight="bold" aria-hidden="true" /><span>Showed up. Our co-founders were at IIT Delhi during the programme.</span></li>
          </ul>
        </Rise>
        <Rise className="pg-mrole__col pg-mrole__col--not" delay={0.08}>
          <h3>What GRID did not do</h3>
          <ul>
            <li><X weight="bold" aria-hidden="true" /><span>Own, organise or run Metamorphosis. Tale of Humankind Foundation and IIT Delhi&apos;s Office of Academic Outreach &amp; New Initiatives did.</span></li>
          </ul>
        </Rise>
      </div>
      <div className="pg-mrole__origin">
        <ClipPhoto
          src="/images/grid-photo-06-p7.jpg"
          alt="GRID co-founders Moksh Sindhwani and Tanishq Aggarwal at Metamorphosis"
          caption="Co-founders Moksh Sindhwani and Tanishq Aggarwal during Metamorphosis."
          sizes="(max-width: 899px) 100vw, 45vw"
        />
        <div>
          <span className="gh-mono">How we know the organisers</span>
          <p>Moksh and Tanishq attended the Young Changemakers Bootcamp at IIT Delhi, run by Tale of Humankind Foundation. GRID started after that, in December 2025.</p>
          <GoLink to="/journey">Read our story</GoLink>
        </div>
      </div>
    </section>
  );
}

function Sources() {
  const [toh, iitd] = verifiedPartnerRelationships;
  return (
    <section className="pg-msrc" aria-labelledby="pg-msrc-title">
      <MaskWords id="pg-msrc-title" text="Go to the organisers." />
      <ul className="pg-bigrows">
        <li><a href={SITE} target="_blank" rel="noreferrer"><span>Metamorphosis</span><small>The programme&apos;s own site</small><ArrowUpRight weight="bold" aria-hidden="true" /></a></li>
        <li><a href={toh.website} target="_blank" rel="noreferrer"><span>{toh.name}</span><small>Organiser</small><ArrowUpRight weight="bold" aria-hidden="true" /></a></li>
        <li><a href={iitd.website} target="_blank" rel="noreferrer"><span>IIT Delhi, Office of Academic Outreach &amp; New Initiatives</span><small>Organiser</small><ArrowUpRight weight="bold" aria-hidden="true" /></a></li>
      </ul>
      <div className="gh-actions">
        <GoLink to="/partnerships">Every programme credit</GoLink>
        <GoLink to="/programmes">All programmes</GoLink>
      </div>
    </section>
  );
}

export default function MetamorphosisPage() {
  return (
    <PageFrame className="rd-page pg-page pg-meta-page">
      <Seo
        title="Metamorphosis"
        path="/programmes/metamorphosis"
        description="Metamorphosis is led by the Tale of Humankind Foundation and IIT Delhi's Office of Academic Outreach & New Initiatives. Project GRID was the outreach partner: 150 students from 18 states, one week at IIT Delhi, 36 products."
      />
      <main className="pg-meta">
        <Hero />
        <CrowdWall />
        <Figures />
        <Moments />
        <Gallery />
        <Role />
        <Sources />
      </main>
    </PageFrame>
  );
}
